import React from 'react'
import { Header, TableComplete } from '@e3dc-react/shell/Elements/Table'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { nullsLast } from '@e3dc-react/shell/Libs/SortAlgorithms'
import { Box, useMediaQuery, useTheme } from '@mui/material'
import { PowermeterHardwareModel } from 'src/Libs/power-meters/models/power-meter.model'
import List from '@e3dc-react/shell/Elements/List/List'

interface PowermeterTableProps {
  powermeters: PowermeterHardwareModel[]
}

/**
 * Component that holds a table with all powermeters of a storage
 */
const PowermeterTable: React.FC<PowermeterTableProps> = ({ powermeters }) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const showListView = useMediaQuery(theme.breakpoints.down('sm'))

  const powermeterTableColumns: Header<PowermeterHardwareModel>[] = [
    {
      label: t('common.id'),
      value: 'id',
      valueRender: powermeterId => {
        return t('system-details.components.powermeters.prefixed-id', { id: powermeterId })
      },
      isSortable: true,
      sortFunction: nullsLast((a: number, b: number) => a - b),
    },
    {
      value: 'type',
      label: t('system-details.components.powermeters.type'),
      isSortable: true,
      sortFunction: nullsLast((a: number, b: number) => a - b),
      valueRender: value => value ?? '-',
    },
    {
      value: 'deviceId',
      label: t('system-details.components.powermeters.assembly-number'),
      isSortable: true,
      sortFunction: nullsLast((a: number, b: number) => a - b),
      valueRender: value => value ?? '-',
    },
    {
      value: 'softwareVersion',
      label: t('common.softwareVersion'),
      isSortable: true,
      sortFunction: nullsLast((a: number, b: number) => a - b),
      valueRender: value => value ?? '-',
    },
    {
      value: 'modelType',
      label: t('system-details.components.powermeters.model-type'),
      sortFunction: nullsLast((a: string, b: string) => a.localeCompare(b)),
      valueRender: value => value ?? '-',
    },
  ]

  if (showListView) {
    return (
      <Box sx={theme => ({ paddingBottom: theme.spacing(2) })}>
        <List
          data={powermeters.map(item => ({
            primary: t('system-details.components.powermeters.prefixed-id', { id: item.id }),
            secondary: item.modelType,
          }))}
          noDataText={t('system-details.components.powermeters.no-data')}
        />
      </Box>
    )
  }

  return (
    <TableComplete
      header={powermeterTableColumns}
      data={powermeters ?? []}
      headerElevation={0}
      bodyElevation={0}
      footerElevation={0}
      coloredColumn
      disablePagination
      noDataText={t('system-details.components.powermeters.no-data')}
    />
  )
}

export default PowermeterTable
